import React, { useEffect, useState } from "react";
import { api, formatError, TIPOS_COMBUSTIVEL, COMBUSTIVEL_LABEL } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";

const TIPOS_VEICULO = [
  { value: "Veiculo_Leve", label: "Veículo Leve" },
  { value: "Caminhao", label: "Caminhão" },
  { value: "Onibus", label: "Ônibus" },
  { value: "Trator", label: "Trator" },
  { value: "Maquina_Pesada", label: "Máquina Pesada" },
  { value: "Outro", label: "Outro" },
];

const empty = {
  placa: "", modelo: "", marca: "", tipo: "Veiculo_Leve",
  combustivel_padrao: "Diesel", hodometro: 0, lotacao: "", observacoes: "", ativo: true,
};

export default function Vehicles() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin", "almoxarife", "tesoureiro");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/vehicles");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, placa: form.placa.toUpperCase().trim(), hodometro: parseFloat(form.hodometro) || 0 };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Salvo");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!confirm("Excluir veículo?")) return;
    try { await api.delete(`/vehicles/${id}`); load(); toast.success("Excluído"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = items.filter((v) => {
    if (!q) return true;
    const nq = normalize(q);
    return normalize(v.placa).includes(nq) || normalize(v.modelo).includes(nq) || normalize(v.marca).includes(nq) || normalize(v.lotacao).includes(nq) || normalize(v.tipo).includes(nq);
  });

  return (
    <div>
      <SectionTitle action={canEdit && <Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-vehicle-btn"><Plus className="w-4 h-4" />Novo Veículo</Btn>}>
        Veículos · <Truck className="inline w-5 h-5 -mt-1" />
      </SectionTitle>
      <div className="mb-4 flex gap-2">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar por placa, marca, modelo ou lotação..." testid="vehicles-search" />
      </div>
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Placa</th><th>Veículo</th><th>Tipo</th><th>Combustível</th><th className="text-right">Hodômetro</th><th>Lotação</th><th>Status</th>{canEdit && <th className="w-24">Ações</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={q ? "Nenhum resultado." : "Nenhum veículo cadastrado."} cols={canEdit ? 8 : 7} />}
            {filtered.map((v) => (
              <tr key={v.id} data-testid={`vehicle-${v.placa}`}>
                <td className="font-mono-data font-bold">{v.placa}</td>
                <td>{[v.marca, v.modelo].filter(Boolean).join(" ")}</td>
                <td className="text-sm">{TIPOS_VEICULO.find((t) => t.value === v.tipo)?.label || v.tipo}</td>
                <td className="text-sm">{COMBUSTIVEL_LABEL[v.combustivel_padrao] || v.combustivel_padrao}</td>
                <td className="text-right font-mono-data">{v.hodometro} km</td>
                <td className="text-sm text-[#555]">{v.lotacao || "—"}</td>
                <td>{v.ativo ? <Badge variant="success">Ativo</Badge> : <Badge variant="default">Inativo</Badge>}</td>
                {canEdit && <td>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(v); setForm({ ...v }); setOpen(true); }} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(v.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar Veículo" : "Novo Veículo"}>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <Field label="Placa *" required value={form.placa} onChange={(v) => setForm({ ...form, placa: v.toUpperCase() })} placeholder="ABC-1234" />
          <Field label="Marca" value={form.marca} onChange={(v) => setForm({ ...form, marca: v })} placeholder="Toyota" />
          <Field label="Modelo *" required value={form.modelo} onChange={(v) => setForm({ ...form, modelo: v })} placeholder="Hilux" />
          <SelectField label="Tipo" value={form.tipo} onChange={(v) => setForm({ ...form, tipo: v })} options={TIPOS_VEICULO} />
          <SelectField label="Combustível Padrão" value={form.combustivel_padrao} onChange={(v) => setForm({ ...form, combustivel_padrao: v })} options={TIPOS_COMBUSTIVEL.map((t) => ({ value: t, label: COMBUSTIVEL_LABEL[t] }))} />
          <Field label="Hodômetro (km)" type="number" value={form.hodometro} onChange={(v) => setForm({ ...form, hodometro: v })} />
          <Field label="Lotação" value={form.lotacao} onChange={(v) => setForm({ ...form, lotacao: v })} placeholder="KM 117 / Cuparí / …" colspan />
          <TextArea label="Observações" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} colspan />
          <div className="flex items-center gap-2 col-span-2">
            <input type="checkbox" id="ativo" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
            <label htmlFor="ativo" className="text-sm">Ativo</label>
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border-2 border-[#111] font-display font-bold uppercase text-xs tracking-wider">Cancelar</button>
            <Btn type="submit">Salvar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
